<template>
  <div>
    <static-alert v-if="message" :message="message" @on-hide="message=''" />

    <!-- Active filters -->
    <div v-if="activeFilters.length" class="mb-2 d-flex flex-wrap gap-1">
      <span
        v-for="f in activeFilters" :key="f.key"
        class="badge bg-secondary d-inline-flex align-items-center gap-1"
      >
        {{ f.label }}
        <button
          type="button" class="btn-close btn-close-white ms-1"
          style="font-size: 0.5em;" aria-label="Remove"
          @click="removeFilter(f.key)"
        />
      </span>
    </div>

    <!-- Search bar: order ID + submit + filter toggle — always one line -->
    <div class="d-flex gap-2 mb-3 position-relative">
      <form class="d-flex gap-2 flex-grow-1" @submit.prevent="handleSearch">
        <input
          v-model.trim="searchOrderId"
          type="search"
          @search="handleSearchClear"
          class="form-control"
          placeholder="Search by order ID"
        />
        <button type="submit" class="btn btn-primary text-nowrap">Search</button>
      </form>
      <button
        type="button"
        class="btn btn-outline-secondary text-nowrap"
        @click="toggleFilters"
      >
        Filters
        <span v-if="activeFilters.length" class="badge bg-primary ms-1">{{ activeFilters.length }}</span>
      </button>

      <!-- Filter dropdown panel -->
      <div v-if="showFilters" class="filter-panel border rounded shadow-sm bg-white p-3">
        <cross-listing-gate>
          <div class="mb-3">
            <div class="fw-semibold small mb-1">Platform</div>
            <div class="form-check" v-for="p in platforms" :key="p.value">
              <input
                class="form-check-input" type="radio" name="platform"
                :id="'plat-' + p.value"
                :value="p.value"
                v-model="pendingPlatform"
              />
              <label class="form-check-label" :for="'plat-' + p.value">{{ p.label }}</label>
            </div>
          </div>
        </cross-listing-gate>
        <div class="mb-2">
          <div class="fw-semibold small mb-1">Status</div>
          <div class="form-check" v-for="s in statuses" :key="s.value">
            <input
              class="form-check-input" type="radio" name="status"
              :id="'stat-' + s.value"
              :value="s.value"
              v-model="pendingStatus"
            />
            <label class="form-check-label" :for="'stat-' + s.value">{{ s.label }}</label>
          </div>
        </div>
        <hr class="my-2" />
        <div class="mb-2">
          <div class="form-check">
            <input
              class="form-check-input" type="checkbox"
              id="showHidden"
              v-model="pendingShowHidden"
            />
            <label class="form-check-label" for="showHidden">Show Hidden Orders</label>
          </div>
        </div>
        <hr class="my-2" />
        <div class="d-flex justify-content-end gap-2">
          <button type="button" class="btn btn-sm btn-outline-secondary" @click="clearFilters">Clear</button>
          <button type="button" class="btn btn-sm btn-primary" @click="applyFilters">Apply</button>
        </div>
      </div>
    </div>

    <hr>

    <transition mode="out-in">
      <loading-spinner v-if="loading" />
      <div v-else-if="total === 0">
        <div class="alert alert-info  p-5 mb-3 text-center">
          <strong>No orders found.</strong>
          Try adjusting your search or filters.
        </div>
      </div>
      <div v-else>
        <paginator-bar
          :page="currentPage"
          :per-page="perPage"
          :total-items="total"
          variant="top"
          @next-page="handlePageChange"
        />
        <div class="grid-container">
          <div v-for="order in orders" :key="order.order_id">
            <order-card :order="order" class="mx-auto" @order-changed="c => loadOrders(c)" />
          </div>
        </div>
        <paginator-bar
          :page="currentPage"
          :per-page="perPage"
          :total-items="total"
          @next-page="handlePageChange"
        />
      </div>
    </transition>
  </div>
</template>

<script setup>
import { ref, computed } from "vue";
import { useStore } from "vuex"
import StaticAlert from "@/app/components/Utils/StaticAlert.vue";
import LoadingSpinner from "@/app/components/Utils/LoadingSpinner.vue";
import OrderCard from "@/app/components/orders/OrderCard.vue";
import PaginatorBar from "@/app/components/Utils/PaginatorBar.vue";
import CrossListingGate from "@/app/components/Utils/CrossListingGate.vue";

const store = useStore()

const orders = ref([])
const message = ref()
const loading = ref(false)
const currentPage = ref(1)
const perPage = ref(30)
const total = ref(0)
const totalPages = ref(0)

const searchOrderId = ref('')
const appliedOrderId = ref('')
const searchPlatform = ref('')
const searchStatus = ref('')
const showFilters = ref(false)
const pendingPlatform = ref('')
const pendingStatus = ref('')
const showHidden = ref(false)
const pendingShowHidden = ref(false)

const platforms = [
  { value: '', label: 'All platforms' },
  { value: 'EBAY', label: 'eBay' },
  { value: 'POSHMARK', label: 'Poshmark' },
  { value: 'DEPOP', label: 'Depop' },
  { value: 'MERCARI', label: 'Mercari' },
]

const statuses = [
  { value: '', label: 'All' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'returned', label: 'Returned' },
]

const activeFilters = computed(() => {
  const filters = []
  if (searchPlatform.value) {
    const p = platforms.find(x => x.value === searchPlatform.value)
    filters.push({ key: 'platform', label: p?.label || searchPlatform.value })
  }
  if (searchStatus.value) {
    const s = statuses.find(x => x.value === searchStatus.value)
    filters.push({ key: 'status', label: s?.label || searchStatus.value })
  }
  if (showHidden.value) {
    filters.push({ key: 'showHidden', label: 'Show Hidden' })
  }
  return filters
})

function removeFilter(key) {
  if (key === 'platform') searchPlatform.value = ''
  if (key === 'status') searchStatus.value = ''
  if (key === 'showHidden') showHidden.value = false
  currentPage.value = 1
  loadOrders()
}

function applyFilters() {
  searchPlatform.value = pendingPlatform.value
  searchStatus.value = pendingStatus.value
  showHidden.value = pendingShowHidden.value
  showFilters.value = false
  currentPage.value = 1
  loadOrders()
}

function clearFilters() {
  const changed = searchPlatform.value !== '' || searchStatus.value !== '' || showHidden.value
  pendingPlatform.value = ''
  pendingStatus.value = ''
  pendingShowHidden.value = false
  searchPlatform.value = ''
  searchStatus.value = ''
  showHidden.value = false
  showFilters.value = false
  if (changed) {
    currentPage.value = 1
    loadOrders()
  }
}

function toggleFilters() {
  if (!showFilters.value) {
    pendingPlatform.value = searchPlatform.value
    pendingStatus.value = searchStatus.value
    pendingShowHidden.value = showHidden.value
  }
  showFilters.value = !showFilters.value
}

function loadOrders(source_only_change) {
  if (source_only_change) return
  loading.value = true
  message.value = ''

  store.dispatch("eshop/getOrdersPaginated", {
    page: currentPage.value,
    perPage: perPage.value,
    orderId: searchOrderId.value || undefined,
    platform: searchPlatform.value || undefined,
    status: searchStatus.value || undefined,
    showHidden: showHidden.value
  })
  .then(data => {
    orders.value = data.orders
    total.value = data.total
    totalPages.value = data.totalPages
    currentPage.value = data.page
    loading.value = false
  })
  .catch(err => {
    message.value = err.response?.error?.message || err.message || JSON.stringify(err)
    loading.value = false
  })
}

function handleSearch() {
  appliedOrderId.value = searchOrderId.value
  currentPage.value = 1
  loadOrders()
}

function handleSearchClear() {
  if (!searchOrderId.value && appliedOrderId.value) {
    appliedOrderId.value = ''
    currentPage.value = 1
    loadOrders()
  }
}

function handlePageChange(direction) {
  currentPage.value = currentPage.value + direction
  loadOrders()
}

// Initial load
loadOrders()
</script>

<style scoped>
.filter-panel {
  position: absolute;
  top: 100%;
  right: 0;
  z-index: 10;
  min-width: 200px;
}
</style>
